import { defineStore } from "pinia";
import { ref, readonly } from "vue";

// entities
import { OrderType } from "@/entities/common/interface/domain/enums";

// widgets
import { STUDENT_SEARCH_STATUS_LIST } from "@/widgets/search-students/config/const";
import type { StudentSortOption, StudentStatus } from "../interface";
import type { StatusItem } from "./interface";

const getDefaultSortOrder = (option: StudentSortOption): OrderType => {
    return option === "name" ? OrderType.Asc : OrderType.Desc;
};

export const useSearchStudentContextStore = defineStore("searchStudentContextStore", () => {
    // 공용 filter 값을 설정합니다.
    const currentStatus = ref<StudentStatus>("academyStudent");
    const currentSearchedStudentId = ref<string | undefined>(undefined);
    const currentSearchedCandidateStudentId = ref<string | undefined>(undefined);
    const currentSearchedFormerStudentId = ref<string | undefined>(undefined);

    const keyword = ref("");
    const keywordInvalid = ref<string | undefined>(undefined);
    const keywordChangeCount = ref<number>(0);
    const sortOption = ref<StudentSortOption>("connectedAt");
    const sortOrder = ref<OrderType>(getDefaultSortOrder("connectedAt"));
    const sortChangeCount = ref<number>(0);

    const statusListRef = ref<StatusItem[]>([...STUDENT_SEARCH_STATUS_LIST]);

    // 네비게이션 클릭 시 resetTrigger 발동
    const shouldResetTrigger = ref<boolean>(false);

    const setCurrentStatus = (status: StudentStatus) => {
        currentStatus.value = status;
    };

    const setKeyword = (word: string) => {
        keyword.value = word;
        keywordChangeCount.value++;
    };

    const setKeywordInvalid = (word: string | undefined) => {
        keywordInvalid.value = word;
    };

    const setSortOption = (option: StudentSortOption) => {
        if (sortOption.value === option) {
            sortOrder.value =
                sortOrder.value === OrderType.Asc ? OrderType.Desc : OrderType.Asc;
        } else {
            sortOption.value = option;
            sortOrder.value = getDefaultSortOrder(option);
        }

        sortChangeCount.value++;
    };

    const updateStatusTotalCount = (status: StudentStatus, count: number | null) => {
        const statusItem = statusListRef.value.find((item) => item.index === status);
        if (statusItem) {
            statusItem.totalCount = count;
        }
    };

    const setCurrentSearchedStudentId = (id: string | undefined) => {
        currentSearchedStudentId.value = id;
    };

    const setCurrentSearchedCandidateStudentId = (id: string | undefined) => {
        currentSearchedCandidateStudentId.value = id;
    };

    const setCurrentSearchedFormerStudentId = (id: string | undefined) => {
        currentSearchedFormerStudentId.value = id;
    };

    const triggerReset = () => {
        shouldResetTrigger.value = !shouldResetTrigger.value;
    };

    const resetSearchResult = () => {
        // 검색어 초기화
        keyword.value = "";
        keywordInvalid.value = undefined;
        currentSearchedStudentId.value = undefined;
        currentSearchedCandidateStudentId.value = undefined;
        currentSearchedFormerStudentId.value = undefined;

        // 검색 input 창 리셋 트리거
        triggerReset();

        // 학생 상태를 재원생으로 변경
        currentStatus.value = "academyStudent";
        sortOption.value = "connectedAt";
        sortOrder.value = getDefaultSortOrder("connectedAt");
    };

    const resetState = () => {
        keyword.value = "";
        keywordInvalid.value = undefined;
        currentSearchedStudentId.value = undefined;
        currentSearchedCandidateStudentId.value = undefined;
        currentSearchedFormerStudentId.value = undefined;

        triggerReset();
        currentStatus.value = "academyStudent";
        sortOption.value = "connectedAt";
        sortOrder.value = getDefaultSortOrder("connectedAt");

        // 상태 목록 총 개수 초기화
        statusListRef.value.forEach((status) => {
            status.totalCount = null;
        });
    };

    return {
        // State
        currentStatus: readonly(currentStatus),
        statusListRef: readonly(statusListRef),
        keyword: keyword,
        keywordInvalid: readonly(keywordInvalid),
        shouldResetTrigger: readonly(shouldResetTrigger),
        currentSearchedStudentId: readonly(currentSearchedStudentId),
        currentSearchedCandidateStudentId: readonly(currentSearchedCandidateStudentId),
        currentSearchedFormerStudentId: readonly(currentSearchedFormerStudentId),
        keywordChangeCount: readonly(keywordChangeCount),
        sortOption: readonly(sortOption),
        sortOrder: readonly(sortOrder),
        sortChangeCount: readonly(sortChangeCount),

        // Actions
        setCurrentStatus,
        setKeyword,
        setSortOption,
        setKeywordInvalid,
        setCurrentSearchedStudentId,
        setCurrentSearchedCandidateStudentId,
        setCurrentSearchedFormerStudentId,
        updateStatusTotalCount,
        triggerReset,
        resetSearchResult,
        resetState,
    };
});
